import React from "react";
import { Box, Typography, Alert } from "@mui/material";
import type { ComparisonGroup } from "../hooks/useComparisonGroups";

interface ComparisonColumnsProps {
  groups: ComparisonGroup[];
  /** Render the per-group content (stat cards, gauges, distribution, etc.). */
  children: (group: ComparisonGroup, index: number) => React.ReactNode;
  /** Number of selected values dropped due to the group cap (for the notice). */
  overflowCount?: number;
  /** Show a coloured label header above each column. Default true. */
  showHeaders?: boolean;
}

/**
 * Lays group content out as side-by-side columns (small multiples). Each column
 * carries a colour-coded label header so a group's identity is consistent with
 * its line colour on overlaid trend charts. Single-group (average) mode renders
 * as one full-width column with no header.
 */
export const ComparisonColumns: React.FC<ComparisonColumnsProps> = ({
  groups,
  children,
  overflowCount = 0,
  showHeaders = true,
}) => {
  const isComparing = groups.length > 1;

  return (
    <Box>
      {overflowCount > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Showing the first {groups.length} groups. {overflowCount} more{" "}
          {overflowCount === 1 ? "is" : "are"} hidden — narrow your selection to
          compare fewer at once.
        </Alert>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
          alignItems: "stretch",
        }}
      >
        {groups.map((group, index) => (
          <Box
            key={group.key}
            sx={{
              flex: 1,
              minWidth: 0,
              // Subtle divider between columns when comparing.
              ...(isComparing && {
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                p: 1.5,
              }),
            }}
          >
            {isComparing && showHeaders && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1.5,
                  pb: 1,
                  borderBottom: "2px solid",
                  borderColor: group.color,
                }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "2px",
                    backgroundColor: group.color,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="subtitle2" fontWeight={700} noWrap>
                  {group.label}
                </Typography>
              </Box>
            )}
            {children(group, index)}
          </Box>
        ))}
      </Box>
    </Box>
  );
};
